import React, { useMemo } from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { Text } from '@/components/common/text';
import { LinkedAccount } from '@/types/residents/home';
import { UserType } from '@/types/residents/user';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '../GlassCard';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { LinkedAccountDetailRow } from './LinkedAccountDetailRow';
import { getDisplayName, getInitials } from '@/utils/residents/utils';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { linkedAccountsStrings } from '@/constants/residents/strings';
import { getAvatarColorById } from '@/utils/residents/avatarColors';

function getRoleLabel(type: UserType): string {
  switch (type) {
    case UserType.RESIDENT: return 'Resident';
    case UserType.GUEST: return 'Guest';
    case UserType.GUARANTOR: return 'Guarantor';
    default: return 'User';
  }
}

interface LinkedAccountCardProps {
  account: LinkedAccount;
  onRemove: (account: LinkedAccount) => void;
  onEmailPress?: (email: string) => void;
}

export const LinkedAccountCard: React.FC<LinkedAccountCardProps> = React.memo(({
  account,
  onRemove,
  onEmailPress,
}) => {
  const { colors, mode } = useThemeColors();
  const { user, sender, property, connectedSince } = account;
  const displayName = getDisplayName(user.firstName, user.lastName);
  const initials = getInitials(user.firstName, user.lastName);
  const avatarColor = useMemo(() => getAvatarColorById(user.id, mode), [user.id, mode]);
  const roleLabel = getRoleLabel(user.type);

  const isEmailOnly = !displayName;
  const nameText = displayName || user.email;

  const handleNamePress = () => {
    if (isEmailOnly && user.email.length > 20 && onEmailPress) {
      onEmailPress(user.email);
    }
  };

  const permissionText = sender
    ? linkedAccountsStrings.cell.sender
    : linkedAccountsStrings.cell.capabilityGuest;

  const connectedDate = useMemo(() => {
    if (!connectedSince) return undefined;
    const date = new Date(connectedSince);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }, [connectedSince]);

  return (
    <GlassCard borderRadius={16}>
      {/* Header: Avatar + Name/Role + More Menu */}
      <View style={styles.header}>
        <View style={[styles.avatar, { backgroundColor: avatarColor.bg }]}>
          {user.photoUrl ? (
            <Image source={{ uri: user.photoUrl }} style={styles.avatarImage} />
          ) : initials ? (
            <Text scale="micro" style={[styles.avatarInitials, { color: avatarColor.text }]}>{initials}</Text>
          ) : (
            <RESIDENTS_SVG_COMPONENTS.USER width={20} height={20} color={avatarColor.text} />
          )}
        </View>

        <View style={styles.nameContainer}>
          <Hoverable
            hover="none"
            onPress={handleNamePress}
            disabled={!isEmailOnly || user.email.length <= 20}
          >
            {/* An email shown as the name is an identifier: one line, middle ellipsis. A real name may wrap. */}
            <Text style={[styles.name, { color: colors.text.tertiary }]} numberOfLines={isEmailOnly ? 1 : 2} ellipsizeMode={isEmailOnly ? 'middle' : 'tail'}>
              {nameText}
            </Text>
          </Hoverable>
          <Text style={[styles.role, { color: colors.text.dimmer }]}>{roleLabel}</Text>
        </View>

        <Hoverable
          hover="glass"
          style={[styles.moreButton, { backgroundColor: colors.glass.buttonBg }]}
          onPress={() => onRemove(account)}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <RESIDENTS_SVG_COMPONENTS.THREE_DOTS width={16} height={16} color={colors.text.dimmer} />
        </Hoverable>
      </View>

      {/* Detail Rows */}
      <View style={[styles.detailsContainer, { borderTopColor: colors.separator }]}>
        <LinkedAccountDetailRow
          icon={<RESIDENTS_SVG_COMPONENTS.DOLLAR width={14} height={14} color={colors.accent.green} />}
          iconBgColor={colors.accent.greenLight}
          label={permissionText}
        />

        {property && (
          <LinkedAccountDetailRow
            icon={<RESIDENTS_SVG_COMPONENTS.HOUSE width={14} height={14} color={PRIMITIVE_COLORS.resioBlue} />}
            iconBgColor={colors.accent.blueBgLight}
            label="Lease:"
            value={property.name}
          />
        )}

        {connectedDate && (
          <LinkedAccountDetailRow
            icon={<RESIDENTS_SVG_COMPONENTS.CALENDAR width={14} height={14} color={colors.text.dimmer} />}
            iconBgColor={colors.activityIcon.bg}
            label="Connected since"
            value={connectedDate}
          />
        )}
      </View>
    </GlassCard>
  );
});

LinkedAccountCard.displayName = 'LinkedAccountCard';

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  avatarInitials: {
    fontSize: 16,
    fontWeight: '700',
  },
  nameContainer: {
    flex: 1,
    gap: 2,
  },
  name: {
    fontSize: 15,
    fontWeight: '600',
  },
  role: {
    ...REDESIGN_TYPOGRAPHY.label,
  },
  moreButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  detailsContainer: {
    marginTop: 16,
    paddingTop: 17,
    borderTopWidth: 1,
    gap: 2,
  },
});
